// ── useOddsStream — React hook over the per-gameKey SSE hub ────────
//
// Thin wrapper around `oddsStreamHub` exposing the per-gameKey state
// via `useSyncExternalStore`. Multiple components mounting with the
// same gameKey share ONE EventSource — both GameDetail's consensus
// chart AND the per-source movement page at /sources subscribe
// through this hook without doubling up on hydra's 256-listener cap.
//
// The hub handles open/close lifecycle (including a 1s grace window
// to survive React 18 StrictMode + rapid forward/back navigation).
// This hook just adapts the shared state into React's render loop +
// runs the stale-watchdog effect.

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  getSnapshot,
  subscribe,
  type OddsStreamHubState,
  type OddsStreamStatus,
  type SourcePoint,
} from '../../services/oddsStreamHub';
import type {
  OddsStreamConsensus,
  OddsStreamConsensusHistoryPoint,
  OddsStreamGameState,
  OddsStreamLine,
  OddsStreamSnapshot,
} from '../../services/oddsStream';
import type { ConsensusTrendState } from '../../lib/consensusTrend';
import { analyticsSupports, hasAnalyticsBase } from '../../services/hydraAnalytics';
import { primaryGameStateEnabled } from '../../services/primaryGameState';

export type { OddsStreamStatus };

export interface UseOddsStreamReturn {
  snapshot: OddsStreamSnapshot | null;
  latestConsensus: OddsStreamConsensus[];
  latestGameState: OddsStreamGameState | null;
  consensusPoints: OddsStreamConsensusHistoryPoint[];
  status: OddsStreamStatus;
  droppedDueToBackpressure: number;
  lastHeartbeatMs: number | null;
  /** Latest per-source line keyed by `${sourceId}|${betType}|${period}|${side}`.
   *  Seeded from snapshot.lines, updated on every `line` SSE event.
   *  Empty until the snapshot arrives. */
  linesByKey: Map<string, OddsStreamLine>;
  /** Rolling per-line (ts, cents) buffer per line key. 10-min sliding
   *  window pruned in the hub. Feeds both the table's sparklines and
   *  the SourcesChart's time-axis lines. */
  sparklines: Map<string, SourcePoint[]>;
  /** Sub-cent trend/recency state per consensus bucket, keyed
   *  `${periodTypeId}|${marketType ?? 'result'}` (Full Game result =
   *  `"1|result"`). Feed to `useConsensusGlow` for the recency-brightness /
   *  trend-arrow / sharp-anchor treatments. */
  consensusTrends: Map<string, ConsensusTrendState>;
}

const STALE_THRESHOLD_MS = 20_000;

export function useOddsStream(gameKey: string | null): UseOddsStreamReturn {
  // Core primary owns scores/status. Its retirement contract supplies no
  // Unabated consensus stream, even if the historical analytics base remains
  // configured. Do not open that stream or expose a retained hub snapshot.
  // A Core outage stays unavailable; it is never permission to revive OG.
  const retired = primaryGameStateEnabled();
  // A base that does not serve the per-game SSE (a SuperHydra core declared
  // through VITE_HYDRA_ANALYTICS_CAPS) must never be asked for it either:
  // subscribe to nothing — no endless failing EventSource behind a red
  // "odds: error". Either way the consumer gets the honest terminal status
  // main's #866 hub gives an unconfigured base ('unavailable'), not an
  // 'idle' that reads as a wait which never ends. NO base at all is left to
  // the hub, which answers 'unavailable' itself without opening anything.
  const capsExcluded = hasAnalyticsBase() && !analyticsSupports('odds-stream');
  const closed = retired || capsExcluded;
  const legacyKey = closed ? null : gameKey;
  const subscribeForKey = useCallback(
    (callback: () => void) => {
      if (!legacyKey) return () => {};
      return subscribe(legacyKey, callback);
    },
    [legacyKey],
  );
  const getSnapshotForKey = useCallback(() => getSnapshot(legacyKey), [legacyKey]);
  const hub: OddsStreamHubState = useSyncExternalStore(subscribeForKey, getSnapshotForKey);

  // Local stale flag — flips when heartbeat hasn't ticked in
  // STALE_THRESHOLD_MS. Kept here (not in the hub) so each consumer
  // sees its own timer wall-clock instead of sharing one ticker.
  const [staleOverride, setStaleOverride] = useState<OddsStreamStatus | null>(null);
  useEffect(() => {
    if (hub.status !== 'open' || hub.lastHeartbeatMs === null) {
      setStaleOverride(null);
      return;
    }
    const id = setInterval(() => {
      const sinceMs = Date.now() - (hub.lastHeartbeatMs ?? 0);
      setStaleOverride(sinceMs > STALE_THRESHOLD_MS ? 'stale' : null);
    }, 5_000);
    return () => clearInterval(id);
  }, [hub.status, hub.lastHeartbeatMs]);

  return {
    snapshot: hub.snapshot,
    latestConsensus: hub.latestConsensus,
    latestGameState: hub.latestGameState,
    consensusPoints: hub.consensusPoints,
    status: closed && gameKey ? 'unavailable' : staleOverride ?? hub.status,
    droppedDueToBackpressure: hub.droppedDueToBackpressure,
    lastHeartbeatMs: hub.lastHeartbeatMs,
    linesByKey: hub.linesByKey,
    sparklines: hub.sparklines,
    consensusTrends: hub.consensusTrends,
  };
}
